// The captain backstory on the in-game captain card (R1246,
// ui-map-interface.md §12).
//
// The backstory is the owner's own text, and only the owner reads it
// (monetization.md §3.1, R1240). It therefore never enters the world, the
// snapshot or the wire. The game page reads it from the meta server with
// the session cookie, through `GET /api/wardroom?mode=<the world's mode>`,
// and keys it by the captain's game id, which is the proto Captain id.
//
// The page asks ONE time a world, on the first card open. A page with no
// meta server behind it (the direct mode, a local dev server) has no
// source, so it asks nothing and draws nothing. A failed read writes one
// debug line and draws nothing: the card stands without the backstory.

import { esc } from './esc';

/** The part of a wardroom answer this module reads. */
export interface BackstoryRoster {
  captains: { id: number; backstory: string }[];
}

/** What the read last did. */
type ReadState = 'none' | 'asking' | 'ready' | 'failed';

let source: (() => Promise<BackstoryRoster>) | null = null;
let readState: ReadState = 'none';
const held = new Map<number, string>();
/** The read generation: a new source starts a new one, and an answer of an
 * older generation lands nowhere (R2236). */
let generation = 0;

/**
 * Sets the read that answers the backstories, or null for a page with no
 * meta server. It forgets what the last source answered, so a handover to a
 * world of another mode reads that mode's roster.
 */
export function setBackstorySource(fetch: (() => Promise<BackstoryRoster>) | null): void {
  source = fetch;
  generation++;
  readState = 'none';
  held.clear();
}

/** The backstory of one captain, or '' when none has landed. */
export function backstoryOf(id: number): string {
  return held.get(id) ?? '';
}

/**
 * Starts the read when no read has started. The card calls it on every
 * draw, so the second call and every later one do nothing. `onLand` runs
 * one time when the answer arrives with at least one backstory, so an open
 * card can draw again.
 */
export function askBackstories(onLand?: () => void): void {
  if (!source || readState !== 'none') return;
  readState = 'asking';
  const mine = generation;
  source().then(
    (w) => {
      if (mine !== generation) return;
      held.clear();
      for (const c of w.captains) {
        if (c.backstory) held.set(c.id, c.backstory);
      }
      readState = 'ready';
      if (held.size > 0) onLand?.();
    },
    (err: unknown) => {
      if (mine !== generation) return;
      readState = 'failed';
      console.debug('backstory: the wardroom read failed', err);
    },
  );
}

/**
 * The markup of one backstory: the heading and the text. The text is
 * escaped, and every line break becomes a `<br>`, so the card keeps the
 * lines the owner wrote. An empty text gives no markup.
 */
export function backstoryHtml(heading: string, text: string): string {
  const body = text.replace(/\r\n?/g, '\n').trim();
  if (!body) return '';
  return `<div class="iopens">${esc(heading)}</div>`
    + `<div class="istory">${esc(body).replace(/\n/g, '<br>')}</div>`;
}
